import { z } from 'zod';

export const documentExtensionSchema = z.enum([
  'swot',
  'pestel',
  'needsWait',
  'paOperational',
  'auditPlanning',
  'auditReport',
  'nonConformity',
  'managementReview',
]);
export type DocumentExtension = z.infer<typeof documentExtensionSchema>;

export const documentDtoSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string().min(1).max(255),
  serialization: z.any().nullable(),
  extension: documentExtensionSchema,
  strictValidation: z.boolean(),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const documentCreateSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'Le nom du document est obligatoire' })
    .max(255, { message: 'Le nom ne doit pas dépasser 255 caractères' }),
  extension: documentExtensionSchema,
  serialization: z.any().optional(),
  strictValidation: z.boolean().optional().default(false),
});

export const documentUpdateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  serialization: z.any().optional(),
  strictValidation: z.boolean().optional(),
  active: z.boolean().optional(),
});

export const documentListQuerySchema = z.object({
  extension: documentExtensionSchema.optional(),
  active: z
    .string()
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});

export const documentValueSiteRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  city: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
});

export const documentValueModuleRefSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
});

/** Entrée pour lier sites/modules à un sous-élément (SWOT/PESTEL), identifié par `itemRef` (= clientKey). */
export const documentValueItemSiteModulesInputSchema = z.object({
  itemRef: z.string().uuid(),
  siteIds: z.array(z.string().uuid()),
  moduleIds: z.array(z.string().uuid()),
});

export type DocumentValueItemSiteModulesInput = z.infer<
  typeof documentValueItemSiteModulesInputSchema
>;

export const documentValueItemSiteModulesDtoSchema = z.object({
  itemRef: z.string().uuid(),
  sites: z.array(documentValueSiteRefSchema),
  modules: z.array(documentValueModuleRefSchema),
});

export const documentValueDtoSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  serialization: z.any().nullable(),
  sites: z.array(documentValueSiteRefSchema).default([]),
  modules: z.array(documentValueModuleRefSchema).default([]),
  itemSiteModules: z.array(documentValueItemSiteModulesDtoSchema).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const documentValueCreateSchema = z.object({
  serialization: z.any().optional(),
  siteIds: z.array(z.string().uuid()).optional(),
  moduleIds: z.array(z.string().uuid()).optional(),
  itemSiteModules: z.array(documentValueItemSiteModulesInputSchema).optional(),
});

export const documentValueUpdateSchema = z.object({
  serialization: z.any(),
  siteIds: z.array(z.string().uuid()).optional(),
  moduleIds: z.array(z.string().uuid()).optional(),
  itemSiteModules: z.array(documentValueItemSiteModulesInputSchema).optional(),
});

export type DocumentDto = z.infer<typeof documentDtoSchema>;
export type DocumentCreateInput = z.infer<typeof documentCreateSchema>;
export type DocumentUpdateInput = z.infer<typeof documentUpdateSchema>;
export type DocumentListQuery = z.infer<typeof documentListQuerySchema>;
export type DocumentValueDto = z.infer<typeof documentValueDtoSchema>;
export type DocumentValueCreateInput = z.infer<typeof documentValueCreateSchema>;
export type DocumentValueUpdateInput = z.infer<typeof documentValueUpdateSchema>;
